import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  ViewChild,
} from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import {
  ChatTask,
  ChatContact,
  NotificationTypeEnum,
  ChatMsg,
} from "src/app/shared/models/chat.model";
import { DataElement } from "src/app/shared/models/common";
import { AuthService } from "src/app/shared/services/auth.service";
import { BroshService } from "src/app/shared/services/brosh.service";
import { ChatService } from "src/app/shared/services/chat.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { ChatTab } from "../order-summary.component";
import { Order } from "src/app/shared/models/order.model";
import { OrderLinesComponent } from "../order-lines/order-lines.component";
import { Store } from "@ngrx/store";
import { ofType, Actions } from "@ngrx/effects";
import { map, take, takeUntil } from "rxjs/operators";
import {
  CREATED_CHAT_TASK,
  CREATE_TASK_FOR_CHAT_MSG,
  RELOAD_CURRENT_ORDER,
  REQUEST_DASHBOARD_REFRESH,
  SELECT_CHAT_CONTACT,
  SELECT_CHAT_TASK,
  SELECT_INBOX_ORDER,
  CLEAR_DISPLAYED_FILE,
} from "src/app/shared/ngrx/ngrx-actions";
import {
  currentChatContact,
  currentOrder,
  qcModeSw,
  userNotifications,
} from "src/app/shared/ngrx/ngrx-selectors";
import { forkJoin, Subject } from "rxjs";
import { ActivityLogComponent } from "../activity-log/activity-log.component";
import { CommonModule } from "@angular/common";
import { OrderFilesComponent } from "../order-files/order-files.component";
import { OrderChatComponent } from "../order-chat/order-chat.component";
import { niceDateFormatPipe } from "src/app/shared/components/ui/nice-date.pipe";
import { LoaderComponent } from "src/app/shared/components/ui/loader/loader.component";
import { OrderNotesComponent } from "../order-notes/order-notes.component";
import { ChatTasksComponent } from "../chat-tasks/chat-tasks.component";
import { OrderEmailsComponent } from "../order-emails/order-emails.component";
import { RxGistComponent } from "../rx-gist/rx-gist.component";
import { Popover } from "primeng/popover";

@Component({
  selector: "summary",
  templateUrl: "./summary.component.html",
  styleUrls: ["./summary.component.scss"],
  imports: [
    OrderLinesComponent,
    ActivityLogComponent,
    CommonModule,
    OrderFilesComponent,
    TranslateModule,
    OrderChatComponent,
    niceDateFormatPipe,
    LoaderComponent,
    OrderNotesComponent,
    ChatTasksComponent,
    OrderEmailsComponent,
    RxGistComponent,
    Popover,
  ],
  standalone: true,
})
export class SummaryComponent {
  @ViewChild(OrderLinesComponent) orderLinesComponent: OrderLinesComponent;
  @ViewChild('custFiles') custFilesComponent: OrderFilesComponent;
  @ViewChild('rxFiles') rxFilesComponent: OrderFilesComponent;
  @ViewChild('participantsPopover') participantsPopover: Popover;

  activityLogLoading: boolean;
  chatTasksLoading: boolean;
  orderHeaderLoading: boolean;
  orderProfileLoading: boolean;
  chatTabs: ChatTab[] = [];
  displayRXFiles: boolean;
  displayCustFiles: boolean;
  displayChatTasks: boolean;
  allowCustFileUpload: boolean;
  displayDrProfile: boolean;
  displayOrigClinic: boolean;
  displayNotes: boolean;
  displayLastSeen: boolean;
  displayProfilePref: boolean;
  displayOrderParams: boolean;
  displayEmails: boolean;
  displayClosedOrder: boolean;
  destroy$ = new Subject();
  currentChatContact$ = this.store.select(currentChatContact);
  private loadedContacts: Set<string> = new Set();
  selectedContact: ChatContact = null;

  get currentOrder(): Order {
    return this.store.selectSignal(currentOrder)();
  }

  get chatDisabled(): boolean {
    return (
      (this.currentOrder.statusCode == "CLINIC_SIGNED" &&
        this.authService.currentUser.isClinicUser) ||
      this.currentOrder.refundSw
    );
  }

  get showNewOrderInstr(): boolean {
    return (
      this.currentOrder.statusCode == "CLINIC_SIGNED" &&
      this.authService.currentUser.isClinicUser
    );
  }

  get showRefundInstr(): boolean {
    return this.currentOrder.refundSw;
  }

  get qcModeSw(): boolean {
    return this.store.selectSignal(qcModeSw)();
  }

  get allowEditLines(): boolean {
    return (
      !this.currentOrder.mmdClosedSw &&
      this.currentOrder.statusCode == "ACTIVE" &&
      this.authService.currentUser.authDetails.getBooleanValue(
        "ORDERS.EDIT_ITEMS_SW"
      ) &&
      !this.currentOrder.refundSw
    );
  }

  get displayActivityLog(): boolean {
    return !this.currentOrder.refundSw;
  }

  get displayFiles(): boolean {
    return !this.currentOrder.refundSw;
  }

  constructor(
    private broshService: BroshService,
    private sharedService: SharedService,
    private chatService: ChatService,
    private authService: AuthService,
    private readonly store: Store,
    private translateService: TranslateService,
    private actions$: Actions
  ) {
    this.displayRXFiles =
      this.authService.currentUser.isPlantUser ||
      this.authService.currentUser.isLabUser;
    this.displayCustFiles = true;
    this.allowCustFileUpload =
      this.authService.currentUser.isClinicUser ||
      this.authService.currentUser.isLabUser;
    this.displayChatTasks =
      this.authService.currentUser.authDetails.getBooleanValue(
        "ORDERS.CHAT_TASKS_SW"
      );
    this.displayOrigClinic =
      this.authService.currentUser.isClinicUser ||
      this.authService.currentUser.isLabUser;

    this.displayNotes = this.authService.currentUser.isLabUser;
    this.displayLastSeen = this.authService.currentUser.isLabUser;
    this.displayEmails = this.authService.currentUser.isLabUser;
    this.displayOrderParams = true;

    this.displayDrProfile =
      this.authService.currentUser.authDetails.getBooleanValue(
        "ORDERS.DR_PROFILE_VISIBLE_SW"
      );
  }

  ngAfterViewInit(): void {
    this.loadOrderData();
    this.handleDueDateChangeAlert();

    this.actions$
      .pipe(
        ofType(SELECT_INBOX_ORDER),
        takeUntil(this.destroy$),
        map(({ }) => {
          // Close any open file dialogs
          if (this.custFilesComponent) {
            this.custFilesComponent.filePopupDisplay = false;
            this.custFilesComponent.selectedFile = null;
          }
          if (this.rxFilesComponent) {
            this.rxFilesComponent.filePopupDisplay = false;
            this.rxFilesComponent.selectedFile = null;
          }

          // Clear the displayed file from store
          this.store.dispatch(CLEAR_DISPLAYED_FILE());

          this.loadOrderData();
          this.handleDueDateChangeAlert();
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(CREATED_CHAT_TASK),
        takeUntil(this.destroy$),
        map(({ }) => {
          this.loadChatTasks();
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(RELOAD_CURRENT_ORDER),
        takeUntil(this.destroy$),
        map(({ }) => {
          if (this.orderLinesComponent != null)
            this.orderLinesComponent.orderLines = this.currentOrder?.orderLines;
          this.orderLinesComponent?.restoreOrigLines();
          // The reload keeps the old tasks, and this route is reused, not
          // re-created: signing an RX can close a task (an intake case's).
          this.loadChatTasks();
        })
      )
      .subscribe();
  }

  handleDueDateChangeAlert(): void {
    if (!this.currentOrder) return;
    if (this.currentOrder.alertDueDateChangeSw) {
      let dueDate;
      if (this.authService.currentUser.isLabUser) {
        dueDate = this.currentOrder.curDueDateFormatted;
      } else if (this.authService.currentUser.isClinicUser) {
        dueDate = this.currentOrder.clinicDueDateFormatted;
      } else {
        dueDate = this.currentOrder.plantDueDateFormatted;
      }
      setTimeout(() => {
        this.sharedService.alertMsg(
          this.translateService.instant("translate.order.dueDateChange"),
          this.translateService.instant("translate.order.newOrderDueDate") +
          " " +
          dueDate
        );
        this.broshService.acceptDueDateChange(this.currentOrder).subscribe();
        this.currentOrder.alertDueDateChangeSw = false;
      }, 500);
    }
  }

  onLastSeenHover(contact: ChatContact, event: MouseEvent) {
    // Only for lab users
    if (!this.authService.currentUser.isLabUser) {
      return;
    }

    this.selectedContact = contact;
    this.participantsPopover.show(event);
  }

  onLastSeenMouseLeave() {
    this.participantsPopover.hide();
  }

  ngOnDestroy(): void {
    this.destroy$.next(1);
  }

  loadOrderData() {
    if (!this.currentOrder) return;

    this.displayClosedOrder =
      this.authService.currentUser.isLabUser && this.currentOrder.mmdClosedSw;

    // Reset loaded contacts tracking for new order
    this.loadedContacts.clear();

    this.chatService.getChatRecipientTypes(this.currentOrder).subscribe(
      (data) => {
        this.currentOrder.chatContacts = data;
        if (this.currentOrder.chatContacts) {
          this.chatTabs = [];
          this.currentOrder.chatContacts.forEach((c) => {
            c.chatTitle = this.getChatTitle(c);
            let chatTab = new ChatTab();
            chatTab.contact = c;
            this.chatTabs.push(chatTab);
          });

          if (this.chatTabs[0]) {
            this.store.dispatch(
              SELECT_CHAT_CONTACT({
                chatContact: this.chatTabs[0].contact,
              })
            );
          }
        }

        // Load participants activity for lab users
        if (this.authService.currentUser.isLabUser && this.currentOrder.chatContacts && this.currentOrder.chatContacts.length > 0) {
          const participantCalls = this.currentOrder.chatContacts.map(contact =>
            this.chatService.getChatParticipantsActivity(this.currentOrder, contact.contactId)
          );

          forkJoin(participantCalls)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
              (results) => {
                results.forEach((participants, index) => {
                  this.currentOrder.chatContacts[index].participantsActivity = participants;
                // Set the chatContact lastSeen to the maximum date from the participantsActivity list
                if (participants && participants.length > 0) {
                  const maxActivity = participants.reduce((max, curr) => {
                    if (!max) return curr;
                    return (curr.lastActivityDate > max.lastActivityDate) ? curr : max;
                  }, null);
                  if (maxActivity && maxActivity.lastActivityDate) {
                    this.currentOrder.chatContacts[index].lastSeen = maxActivity.lastActivityDate;
                  }
                }
                });
              },
              (error) => {
                this.sharedService.alertError(error);
              }
            );
        }

        this.loadOrderDetails();
      },
      (error) => {
        this.sharedService.alertError(error);
        this.loadOrderDetails();
      }
    );
  }

  loadOrderDetails() {
    this.orderLinesComponent?.restoreOrigLines();

    this.activityLogLoading = true;
    this.broshService.getOrderActivityLog(this.currentOrder).subscribe(
      (data) => {
        this.currentOrder.activityLog = data;
        this.activityLogLoading = false;
      },
      (error) => {
        this.activityLogLoading = false;
        this.sharedService.alertError(error);
      }
    );

    this.loadChatTasks();

    this.store
      .select(userNotifications)
      .pipe(take(1))
      .subscribe((userNotifications) => {
        let filesUploadNotifications = userNotifications.filter(
          (n) =>
            n.orderId == this.currentOrder.orderId &&
            n.type == NotificationTypeEnum.FILE_UPLOAD &&
            !n.readSw
        );
        if (filesUploadNotifications.length > 0) {
          this.chatService
            .markUserNotificationRead(filesUploadNotifications)
            .subscribe(() => {
              filesUploadNotifications.forEach((n) => (n.readSw = true));
              this.store.dispatch(REQUEST_DASHBOARD_REFRESH());
            });
        }
      });

    if (this.displayDrProfile) {
      this.orderProfileLoading = true;
      this.broshService.getOrderDrProfile(this.currentOrder.orderId).subscribe(
        (data) => {
          this.currentOrder.drProfile = data;
          this.orderProfileLoading = false;
        },
        (error) => {
          this.orderProfileLoading = false;
          this.sharedService.alertError(error);
        }
      );
    }
  }

  loadChatTasks() {
    if (this.displayChatTasks) {
      this.chatTasksLoading = true;
      this.chatService.getChatTasks(this.currentOrder).subscribe(
        (data) => {
          this.currentOrder.chatTasks = data;
          this.chatTasksLoading = false;
        },
        (error) => {
          this.chatTasksLoading = false;
          this.sharedService.alertError(error);
        }
      );
    }
  }

  reloadOrderHeader() {
    if (!this.currentOrder) return;

    this.orderHeaderLoading = true;
    this.broshService.getOrderHeaderSingle(this.currentOrder.orderId, this.qcModeSw).subscribe(
      (data) => {
        if (data) {
          this.store.dispatch(
            RELOAD_CURRENT_ORDER({
              order: data,
            })
          );
        }
        this.loadOrderDetails();
        this.orderHeaderLoading = false;
      },
      (error) => {
        this.orderHeaderLoading = false;
        this.sharedService.alertError(error);
      }
    );
  }

  onFileUploaded() {
    this.reloadOrderHeader();
  }

  getChatTitle(contact: ChatContact): string {
    if (this.authService.currentUser.org == "LAB") {
      if (contact.contactId == "CLINIC") {
        return "translate.order.chatWithCLINIC";
      } else if (contact.contactId == "PLANT") {
        return "translate.order.chatWithPLANT";
      }
    } else {
      return "translate.chat.currentOrderChatTitle";
    }
  }

  createTask() {
    let newTask = new ChatTask();
    newTask.status = new DataElement("OPEN", "בטיפול");

    this.store.dispatch(
      SELECT_CHAT_TASK({
        task: newTask,
      })
    );
  }

  editChatTask(task: ChatTask) {
    this.store.dispatch(
      SELECT_CHAT_TASK({
        task: task,
      })
    );
  }

  onNoteAdded() {
    this.broshService.getOrderHeaderSingle(this.currentOrder.orderId, this.qcModeSw).subscribe(
      (data) => {
        if (data) {
          this.currentOrder.notes = data.notes;
        }
      },
      (error) => {
        this.sharedService.alertError(error);
      }
    );
  }

  onEmailsUpdated() {
    this.broshService.getOrderHeaderSingle(this.currentOrder.orderId, this.qcModeSw).subscribe(
      (data) => {
        if (data) {
          this.currentOrder.orderEmails = data.orderEmails;
        }
      },
      (error) => {
        this.sharedService.alertError(error);
      }
    );
  }

  onFirstMessagesLoaded(contact: ChatContact) {
    // Track that this contact has loaded its messages
    this.loadedContacts.add(contact.contactId);

    // Only proceed if all chat contacts have loaded their messages
    if (this.loadedContacts.size === this.chatTabs.length) {
      if (this.authService.currentUser.isLabUser) {
        // For lab users, check if PLANT contact has unread messages
        const plantContact = this.chatTabs.find(tab => tab.contact.contactId === "PLANT")?.contact;
        const clinicContact = this.chatTabs.find(tab => tab.contact.contactId === "CLINIC")?.contact;

        // If CLINIC contact has unread messages, select it
        if (clinicContact && clinicContact.unreadCnt > 0) {
          this.selectChatContact(clinicContact);
        }
        // Otherwise, if PLANT contact has unread messages, select it
        else if (plantContact && plantContact.unreadCnt > 0) {
          this.selectChatContact(plantContact);
        }
      }
    }
  }

  selectChatContact(contact: ChatContact) {
    this.store.dispatch(
      SELECT_CHAT_CONTACT({
        chatContact: contact,
      })
    );
  }

  createChatTask(msg: ChatMsg) {
    this.store.dispatch(
      CREATE_TASK_FOR_CHAT_MSG({
        msg: msg,
      })
    );
  }
}
